import React, { Fragment, useEffect, useState } from 'react';
import {
  DocumentDuplicateIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import TextWinds from '../../../components/common/TextWinds';
import { LmsInfoTypes, LmsVoucherProps } from '../../../constants/types';
import TodoTicketStatus from './TodoTicketStatus';
import instance from '../../../api/axios/utils.ts/instance';
import { useAppDispatch } from '../../../store/hook';
import { openAlert, openModal } from '../../../store/features/modalSlice';
import ModalExtendLink from '../../../components/modal/ModalExtendLink';
import {
  setExtendBtnLink,
  setPurchaseTicketLink,
} from '../../../api/utils/lms/ExtendSetBtnLink';
import ModalAlert from '../../../components/modal/ModalAlert';
import DashboardCard, { DashboardCardItem } from './DashboardCard';
import HFbutton from '../../../components/common/input/HFbutton';
import {
  changeTimestamp,
  handleCopyClipBoard,
} from '../../../api/utils/util-func';
import LmsBanner from './components/LmsBanner';

interface contentsProps {
  classId: string;
  lmsLists: LmsInfoTypes;
}
const HeaderContents = ({ classId, lmsLists }: contentsProps) => {
  const dispatch = useAppDispatch();

  const extendButtonHandler = ({ id, type }: { id: string; type: string }) => {
    const { checkType, purchaseLink } = setExtendBtnLink(id);
    dispatch(
      openModal({
        body: (
          <ModalExtendLink
            checkType={checkType}
            type={type}
            message="클래스 연장하기"
            purchaseLInk={purchaseLink}
          />
        ),
      }),
    );
  };

  const purchaseTicket = (data: any) => {
    const targetUrl = setPurchaseTicketLink(data.type);
    if ('' !== targetUrl) {
      window.open(targetUrl, '_blank');
    } else {
      dispatch(
        openModal({
          body: (
            <ModalAlert message="이용권 구매를 할 수 없습니다. 관리자에게 문의하세요." />
          ),
        }),
      );
    }
  };

  const [voucherLists, setVoucherLists] =
    useState<LmsVoucherProps[]>(undefined);

  useEffect(() => {
    if (classId === lmsLists.id) {
      const fetchTicketStatus = async () => {
        try {
          await instance
            .get(`/v1/lms/${classId}/todo/voucher`, {
              params: { courseId: lmsLists.course?.id },
            })
            .then((res) => {
              if (200 === res.status) {
                setVoucherLists(res.data.data);
              }
            });
        } catch (error) {
          setVoucherLists(undefined);
        }
      };
      fetchTicketStatus();
    }
  }, [classId]);

  const handleCopyClipboard = (text: string) => {
    handleCopyClipBoard(text).then((result) => {
      if (result) {
        dispatch(openAlert({ message: '클래스 코드가 복사되었습니다.' }));
      } else {
        dispatch(openAlert({ message: '클래스 코드 복사에 실패했습니다.' }));
      }
    });
  };

  return (
    <Fragment>
      {/* 모바일 버전 클래스 정보 */}
      <div className="bg-gray1 xl:hidden">
        <TextWinds type="title_h3" className="mb-[2px]">
          {lmsLists.course?.name}
        </TextWinds>
        <TextWinds
          type="content_body4"
          className="mb-[2px] flex cursor-pointer items-center gap-1 hover:text-skyblue"
          onClick={() => handleCopyClipboard(lmsLists.id)}
        >
          {lmsLists.id}
          <DocumentDuplicateIcon className="h-5 w-5" />
        </TextWinds>
        <TextWinds type="content_body4">
          {lmsLists && new Date(lmsLists.created_at).toLocaleDateString()} 개설
        </TextWinds>
      </div>

      {/* 모바일 버전 안내 문구 */}
      <div className="rounded bg-purple0 px-3 py-[14px] xl:hidden">
        <TextWinds type="caption1" color="purple4">
          본 LMS는 PC에서 이용을 권장합니다. 다양한 LMS 기능들을 모바일 버전보다
          훨씬 편리하게 이용할 수 있어요.
        </TextWinds>
      </div>
      {/* LMS 배너 */}
      <section className="lg:aspect-none m-auto aspect-video w-full max-w-[1920px] lg:max-h-[423px]">
        <LmsBanner lmsLists={lmsLists} />
      </section>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 연장 관리 */}
        <DashboardCard title="연장 관리">
          <div className="h-[100px]">
            <DashboardCardItem title="이용 잔여일">
              <div className="text-red5">
                {Number(lmsLists.remain_days) > 0
                  ? `${lmsLists.remain_days}일 (${changeTimestamp(
                      lmsLists.end_date,
                    )} 까지)`
                  : `${lmsLists.remain_days}일`}
              </div>
            </DashboardCardItem>
          </div>

          <HFbutton
            height={38}
            type="Outlined"
            onClick={() => extendButtonHandler(lmsLists.course)}
          >
            <div className="text-xs">이용 기간 연장하기</div>
          </HFbutton>
          <div className="mt-3 flex gap-2 rounded bg-gray1 p-2">
            <InformationCircleIcon className="h-5 w-5 shrink-0 text-gray6" />
            <div className="text-xs">
              교사의 코스 이용 기간이 활성화되어 있을 때만 수업 운영 및 교재
              구매가 가능합니다. 이용 기간을 연장하여 클래스를 운영해 주세요.
            </div>
          </div>
        </DashboardCard>

        {/* 할 일 이용권 현황 */}
        {voucherLists ? (
          <DashboardCard title="할 일 이용권 현황">
            <TodoTicketStatus
              voucherLists={voucherLists}
              purchaseTicket={purchaseTicket}
            />
          </DashboardCard>
        ) : (
          <div></div>
        )}
      </div>
    </Fragment>
  );
};

export default HeaderContents;
